import type { InvoicesV2QueryParams, InvoicesV2Response } from './types';

import {
  keepPreviousData,
  queryOptions,
  type QueryFunctionContext,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { queryKeysInvoice } from '../queryKeys';

import { fetchInvoicesV2 } from './fetchInvoicesV2';

/**
 * Generates query options for the V2 tenant-scoped invoice list endpoint.
 *
 * @param queryParams - Search, filter, sort, and pagination parameters.
 * @param options - Additional query options.
 * @returns Query options for the V2 invoice list.
 */
// fallow-ignore-next-line unused-export -- canonical query-options API for route loaders
export function getInvoicesV2QueryOptions<TData = InvoicesV2Response>({
  queryParams,
  options,
}: {
  queryParams: Partial<InvoicesV2QueryParams>;
  options?: Omit<
    UseQueryOptions<InvoicesV2Response, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) {
  return queryOptions<InvoicesV2Response, Error, TData>({
    queryFn: (context: QueryFunctionContext) =>
      fetchInvoicesV2(queryParams, context),
    queryKey: queryKeysInvoice.getAllV2(queryParams),
    staleTime: ms('60s'),
    ...options,
  });
}

/**
 * Loads paginated incoming invoices.
 *
 * @param queryParams - Search, filter, sort, and pagination parameters.
 * @param options - Additional query options.
 * @returns TanStack Query result.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/list_invoices_invoices_get
 */
export function useQueryInvoicesV2<TData = InvoicesV2Response>(
  queryParams: Partial<InvoicesV2QueryParams>,
  options?: Omit<
    UseQueryOptions<InvoicesV2Response, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    ...getInvoicesV2QueryOptions<TData>({ options, queryParams }),
    placeholderData: keepPreviousData,
  });
}
